import type { TemplateRunSummary } from '@/types/templates';

export type TemplateRunChain = {
  rootRunId: string;
  runs: TemplateRunSummary[];
  displayRun: TemplateRunSummary;
  rootRun: TemplateRunSummary;
  hasContinuation: boolean;
};

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function readRunDataString(run: Pick<TemplateRunSummary, 'data'>, ...keys: string[]): string | null {
  const data = run.data;
  if (!data || typeof data !== 'object') {
    return null;
  }
  for (const key of keys) {
    const value = readString(data[key]);
    if (value) {
      return value;
    }
  }
  return null;
}

function timeScore(value: string | null | undefined): number {
  if (!value) {
    return 0;
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function compareRunsNewestFirst(left: TemplateRunSummary, right: TemplateRunSummary): number {
  const leftScore = timeScore(left.startedAt);
  const rightScore = timeScore(right.startedAt);
  if (leftScore !== rightScore) {
    return rightScore - leftScore;
  }
  return right.runId.localeCompare(left.runId);
}

export function getTemplateRunResumedFromId(run: Pick<TemplateRunSummary, 'resume' | 'data'>): string | null {
  return (
    readString(run.resume?.resumedFromRunId) ||
    readRunDataString(run, 'resumed_from_run_id', 'resumedFromRunId')
  );
}

export function getTemplateRunRootId(run: TemplateRunSummary): string | null {
  return readString(run.resume?.rootRunId) || readRunDataString(run, 'root_run_id', 'rootRunId');
}

export function getTemplateRunContinuedByRunId(run: TemplateRunSummary): string | null {
  return readRunDataString(run, 'continued_by_run_id', 'continuedByRunId');
}

export function buildTemplateRunChains(runs: TemplateRunSummary[]): TemplateRunChain[] {
  const runById = new Map(runs.map((run) => [run.runId, run]));
  const rootCache = new Map<string, string>();

  const resolveRootRunId = (run: TemplateRunSummary, visited = new Set<string>()): string => {
    const cached = rootCache.get(run.runId);
    if (cached) {
      return cached;
    }
    if (visited.has(run.runId)) {
      return run.runId;
    }
    visited.add(run.runId);

    const explicitRoot = getTemplateRunRootId(run);
    if (explicitRoot) {
      rootCache.set(run.runId, explicitRoot);
      return explicitRoot;
    }

    const parentRunId = getTemplateRunResumedFromId(run);
    if (parentRunId) {
      const parentRun = runById.get(parentRunId);
      const rootRunId = parentRun ? resolveRootRunId(parentRun, visited) : parentRunId;
      rootCache.set(run.runId, rootRunId);
      return rootRunId;
    }

    rootCache.set(run.runId, run.runId);
    return run.runId;
  };

  const grouped = new Map<string, TemplateRunSummary[]>();
  for (const run of runs) {
    const rootRunId = resolveRootRunId(run);
    grouped.set(rootRunId, [...(grouped.get(rootRunId) ?? []), run]);
  }

  return [...grouped.entries()]
    .map(([rootRunId, groupRuns]) => {
      const sortedRuns = [...groupRuns].sort(compareRunsNewestFirst);
      const displayRun = sortedRuns[0];
      if (!displayRun) {
        throw new Error('Template run chain cannot be built without runs.');
      }
      const rootRun = groupRuns.find((run) => run.runId === rootRunId) ?? sortedRuns[sortedRuns.length - 1] ?? displayRun;
      const hasContinuation =
        groupRuns.length > 1 ||
        groupRuns.some((run) => Boolean(getTemplateRunResumedFromId(run) || getTemplateRunContinuedByRunId(run)));

      return {
        rootRunId,
        runs: sortedRuns,
        displayRun,
        rootRun,
        hasContinuation,
      };
    })
    .sort((left, right) => compareRunsNewestFirst(left.displayRun, right.displayRun));
}

export function findTemplateRunChainByRunId(
  chains: TemplateRunChain[],
  runId: string | null,
): TemplateRunChain | null {
  if (!runId) {
    return null;
  }
  return chains.find((chain) => chain.rootRunId === runId || chain.runs.some((run) => run.runId === runId)) ?? null;
}
